<!--
  AI:
  Status: "in progress"
  Type: Component
  SubType: CrudIntegration
  Reason: Execution monitor for LogicFlow executions — displays execution list, step details, and message log with severity filtering and resolution actions.
-->

@if (initialized) {

  <!-- Toolbar -->
  <div class="flex align-items-center justify-content-between mb-3">
    <div class="flex align-items-center gap-2">
      <span class="font-semibold text-lg">Ausführungen</span>
    </div>
    <div class="flex align-items-center gap-2">

      <!-- Status filter -->
      <p-select
        [options]="statusFilterOptions"
        [(ngModel)]="selectedStatusFilter"
        optionLabel="label"
        optionValue="value"
        placeholder="Status filtern"
        (onChange)="onStatusFilterChange()"
        class="w-12rem">
      </p-select>

      <!-- Refresh button -->
      <p-button
        icon="fa-regular fa-arrows-rotate"
        [outlined]="true"
        severity="secondary"
        pTooltip="Aktualisieren"
        tooltipPosition="top"
        [loading]="busy"
        (onClick)="onRefresh()">
      </p-button>
    </div>
  </div>

  <!-- Error state -->
  @if (errorMessage) {
    <div class="flex flex-column align-items-center justify-content-center p-5 surface-ground border-round mb-3">
      <i class="fa-regular fa-triangle-exclamation text-4xl text-red-500 mb-3"></i>
      <span class="text-color-secondary mb-3">{{ errorMessage }}</span>
      <p-button
        label="Erneut versuchen"
        icon="fa-regular fa-arrows-rotate"
        [outlined]="true"
        (onClick)="onRefresh()">
      </p-button>
    </div>
  }

  <!-- Loading state -->
  @if (busy && executionList.length === 0 && !errorMessage) {
    <div class="flex flex-column gap-2">
      @for (i of [1, 2, 3]; track i) {
        <div class="surface-card border-round p-3">
          <p-skeleton width="100%" height="2rem"></p-skeleton>
        </div>
      }
    </div>
  }

  <!-- Empty state -->
  @if (!busy && executionList.length === 0 && !errorMessage) {
    <div class="flex flex-column align-items-center justify-content-center p-5 surface-ground border-round">
      <i class="fa-regular fa-rectangle-list text-4xl text-color-secondary mb-3"></i>
      <span class="text-color-secondary">Keine Ausführungen vorhanden</span>
    </div>
  }

  <!-- Execution list table -->
  @if (executionList.length > 0) {
    <p-table
      [value]="executionList"
      [selectionMode]="'single'"
      [(selection)]="selectedExecution"
      (onRowSelect)="selectExecution($any($event.data))"
      (onRowUnselect)="selectExecution($any($event.data))"
      [rowHover]="true"
      [scrollable]="true"
      scrollHeight="24rem"
      styleClass="p-datatable-sm">

      <ng-template pTemplate="header">
        <tr>
          <th style="width: 10rem">Status</th>
          <th style="width: 8rem">Modus</th>
          <th style="width: 12rem">Gestartet</th>
          <th style="width: 12rem">Beendet</th>
          <th style="width: 8rem">Dauer</th>
          <th>Ergebnis</th>
        </tr>
      </ng-template>

      <ng-template pTemplate="body" let-execution>
        <tr [pSelectableRow]="execution"
            [class.surface-50]="selectedExecution?.id === execution.id">
          <td>
            <p-tag
              [value]="getExecutionStatusConfig(execution.executionStatus).label"
              [severity]="getExecutionStatusConfig(execution.executionStatus).severity"
              [icon]="getExecutionStatusConfig(execution.executionStatus).icon">
            </p-tag>
          </td>
          <td>
            @switch (execution.executionMode) {
              @case (ExecutionMode.DRY_RUN) {
                <p-tag styleClass="bg-yellow-100 text-yellow-800"
                       value="SANDBOX"
                       icon="fa-regular fa-flask"></p-tag>
              }
              @case (ExecutionMode.DEBUG) {
                <p-tag styleClass="bg-blue-100 text-blue-700"
                       value="DEBUG"
                       icon="fa-regular fa-bug"></p-tag>
              }
              @default {
                <!-- NORMAL: empty cell, absence is the signal -->
              }
            }
          </td>
          <td>
            @if (execution.startedAt) {
              <span class="text-sm">{{ execution.startedAt | date:'dd.MM.yyyy HH:mm:ss' }}</span>
            } @else {
              <span class="text-color-secondary">-</span>
            }
          </td>
          <td>
            @if (execution.completedAt) {
              <span class="text-sm">{{ execution.completedAt | date:'dd.MM.yyyy HH:mm:ss' }}</span>
            } @else {
              <span class="text-color-secondary">-</span>
            }
          </td>
          <td>
            <span class="text-sm">{{ formatDuration(calculateDuration(execution.startedAt, execution.completedAt)) }}</span>
          </td>
          <td>
            @if (execution.errorMessage) {
              <span class="text-red-500 text-sm" pTooltip="{{ execution.errorMessage }}" tooltipPosition="top">
                <i class="fa-regular fa-circle-xmark mr-1"></i>Fehler
              </span>
            } @else if (execution.resultDataJson) {
              <span class="text-green-500 text-sm">
                <i class="fa-regular fa-circle-check mr-1"></i>Ergebnis vorhanden
              </span>
            } @else {
              <span class="text-color-secondary text-sm">-</span>
            }
          </td>
        </tr>
      </ng-template>

      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="6" class="text-center text-color-secondary p-4">
            Keine Ausführungen gefunden
          </td>
        </tr>
      </ng-template>
    </p-table>
  }

  <!-- Execution Detail Panel -->
  @if (selectedExecution) {
    @if (selectedExecution.executionMode === ExecutionMode.DRY_RUN) {
      <div class="flex align-items-center gap-2 px-4 py-3 bg-yellow-50 border-bottom-1 border-yellow-200">
        <i class="fa-regular fa-flask text-yellow-500"></i>
        <span class="text-yellow-700 text-sm">
          Sandbox-Lauf — keine echten Datenänderungen.
          Diese Ausführung lief im Trockenlauf-Modus und schreibt nicht in die Datenbank.
        </span>
      </div>
    }
    <div class="mt-4"
         [class.bg-yellow-50]="selectedExecution.executionMode === ExecutionMode.DRY_RUN">

      <!-- Step List Section -->
      <div class="mb-4">
        <div class="flex align-items-center justify-content-between mb-2">
          <span class="font-semibold">Schritte</span>
          @if (loadingSteps) {
            <i class="fa-regular fa-spinner-third fa-spin text-color-secondary"></i>
          }
        </div>

        @if (loadingSteps && executionSteps.length === 0) {
          <div class="flex flex-column gap-2">
            @for (i of [1, 2]; track i) {
              <p-skeleton width="100%" height="3rem"></p-skeleton>
            }
          </div>
        }

        @if (!loadingSteps && executionSteps.length === 0) {
          <div class="flex align-items-center justify-content-center p-3 surface-ground border-round">
            <span class="text-color-secondary text-sm">Keine Schritte vorhanden</span>
          </div>
        }

        @if (executionSteps.length > 0) {
          <div class="flex flex-column gap-1">
            @for (step of executionSteps; track step.id) {
              <!-- Step row header -->
              <div class="surface-card border-round border-1 surface-border">
                <div class="flex align-items-center justify-content-between p-3 cursor-pointer"
                     (click)="toggleStepExpansion(step.id)">
                  <div class="flex align-items-center gap-3">
                    <i class="fa-regular"
                       [class.fa-chevron-right]="!isStepExpanded(step.id)"
                       [class.fa-chevron-down]="isStepExpanded(step.id)">
                    </i>
                    <p-tag
                      [value]="getStepStatusConfig(step.stepExecutionStatus).label"
                      [severity]="getStepStatusConfig(step.stepExecutionStatus).severity"
                      [icon]="getStepStatusConfig(step.stepExecutionStatus).icon">
                    </p-tag>
                    <span class="font-medium">{{ step.flowStepDtoName || ('Schritt #' + step.id) }}</span>
                  </div>
                  <div class="flex align-items-center gap-3">
                    @if (step.durationMs !== null && step.durationMs !== undefined) {
                      <span class="text-color-secondary text-sm">{{ formatDuration(step.durationMs) }}</span>
                    }
                    @if (step.retryCount && step.retryCount > 0) {
                      <p-tag [value]="step.retryCount + ' Wiederholungen'" severity="warn" [rounded]="true"></p-tag>
                    }
                    @if (step.flowStepDto?.useMockOutput === true &&
                         selectedExecution?.executionMode !== ExecutionMode.NORMAL) {
                      <p-tag styleClass="bg-pink-100 text-pink-700"
                             value="MOCKED"
                             icon="fa-regular fa-mask"
                             pTooltip="Dieser Step wurde zur Laufzeit gemockt (useMockOutput=true und Ausführungsmodus != NORMAL)."
                             tooltipPosition="top"></p-tag>
                    }
                  </div>
                </div>

                <!-- Step detail expansion -->
                @if (isStepExpanded(step.id)) {
                  <div class="px-3 pb-3 border-top-1 surface-border">

                    <!-- Error message -->
                    @if (step.errorMessage) {
                      <div class="mt-3 p-3 border-round" style="background-color: var(--red-50);">
                        <div class="flex align-items-center gap-2 mb-2">
                          <i class="fa-regular fa-circle-xmark text-red-500"></i>
                          <span class="font-semibold text-red-700">Fehlermeldung</span>
                        </div>
                        <pre class="text-sm m-0 white-space-pre-wrap text-red-700">{{ step.errorMessage }}</pre>
                      </div>
                    }

                    <!-- Input data -->
                    <div class="mt-3">
                      <span class="text-color-secondary text-sm font-semibold">Eingabedaten</span>
                      @if (step.inputDataJson) {
                        <pre class="text-sm mt-1 p-3 surface-ground border-round overflow-auto" style="max-height: 16rem;">{{ formatJson(step.inputDataJson) }}</pre>
                      } @else {
                        <div class="text-color-secondary text-sm mt-1">Keine Eingabedaten</div>
                      }
                    </div>

                    <!-- Output data -->
                    <div class="mt-3">
                      <span class="text-color-secondary text-sm font-semibold">Ausgabedaten</span>
                      @if (step.outputDataJson) {
                        <pre class="text-sm mt-1 p-3 surface-ground border-round overflow-auto" style="max-height: 16rem;">{{ formatJson(step.outputDataJson) }}</pre>
                      } @else {
                        <div class="text-color-secondary text-sm mt-1">Keine Ausgabedaten</div>
                      }
                    </div>
                  </div>
                }
              </div>
            }
          </div>
        }
      </div>

      <!-- Message Log Section -->
      <div class="mb-4">
        <div class="flex align-items-center justify-content-between mb-2">
          <span class="font-semibold">Nachrichten</span>
          <div class="flex align-items-center gap-1">
            @if (loadingMessages) {
              <i class="fa-regular fa-spinner-third fa-spin text-color-secondary mr-2"></i>
            }
            <!-- Severity filter toggles -->
            @for (filter of severityFilters; track filter.severity; let i = $index) {
              <p-button
                [icon]="filter.icon"
                [outlined]="!filter.active"
                [severity]="filter.active ? getSeverityConfig(filter.severity).severity : 'secondary'"
                [rounded]="true"
                [text]="!filter.active"
                size="small"
                pTooltip="{{ filter.label }}"
                tooltipPosition="top"
                (onClick)="toggleSeverityFilter(i)">
              </p-button>
            }
          </div>
        </div>

        @if (loadingMessages && filteredMessages.length === 0) {
          <p-skeleton width="100%" height="4rem"></p-skeleton>
        }

        @if (!loadingMessages && filteredMessages.length === 0) {
          <div class="flex align-items-center justify-content-center p-3 surface-ground border-round">
            <span class="text-color-secondary text-sm">Keine Nachrichten vorhanden</span>
          </div>
        }

        @if (filteredMessages.length > 0) {
          <p-table
            [value]="filteredMessages"
            [scrollable]="true"
            scrollHeight="16rem"
            styleClass="p-datatable-sm">

            <ng-template pTemplate="header">
              <tr>
                <th style="width: 12rem">Schwere</th>
                <th style="width: 10rem">Code</th>
                <th>Nachricht</th>
                <th style="width: 12rem">Aufgelöst</th>
                <th style="width: 8rem">Aktionen</th>
              </tr>
            </ng-template>

            <ng-template pTemplate="body" let-message>
              <tr>
                <td>
                  <p-tag
                    [value]="getSeverityConfig(message.severity).label"
                    [severity]="getSeverityConfig(message.severity).severity"
                    [icon]="getSeverityConfig(message.severity).icon">
                  </p-tag>
                </td>
                <td>
                  @if (message.messageCode) {
                    <span class="text-sm font-mono">{{ message.messageCode }}</span>
                  } @else {
                    <span class="text-color-secondary">-</span>
                  }
                </td>
                <td>
                  <span class="text-sm">{{ message.messageText }}</span>
                </td>
                <td>
                  @if (message.resolved) {
                    <div class="flex flex-column">
                      <p-tag value="Aufgelöst" severity="success" icon="fa-regular fa-check" [rounded]="true"></p-tag>
                      @if (message.resolvedByUserDtoName) {
                        <span class="text-xs text-color-secondary mt-1">{{ message.resolvedByUserDtoName }}</span>
                      }
                      @if (message.resolvedAt) {
                        <span class="text-xs text-color-secondary">{{ message.resolvedAt | date:'dd.MM.yyyy HH:mm' }}</span>
                      }
                    </div>
                  } @else {
                    <span class="text-color-secondary text-sm">Offen</span>
                  }
                </td>
                <td>
                  @if (message.severity === MessageSeverity.DECISION_REQUIRED && !message.resolved) {
                    <p-button
                      label="Auflösen"
                      icon="fa-regular fa-circle-check"
                      [outlined]="true"
                      severity="help"
                      size="small"
                      (onClick)="resolveMessage(message)">
                    </p-button>
                  }
                </td>
              </tr>
            </ng-template>
          </p-table>
        }
      </div>

      <!-- Context / Result Data (collapsible) -->
      @if (selectedExecution.contextDataJson || selectedExecution.resultDataJson) {
        <div class="mb-4">
          <p-accordion>
            @if (selectedExecution.contextDataJson) {
              <p-accordion-panel value="context">
                <p-accordion-header>Kontextdaten</p-accordion-header>
                <p-accordion-content>
                  <pre class="text-sm p-3 surface-ground border-round overflow-auto" style="max-height: 20rem;">{{ formatJson(selectedExecution.contextDataJson) }}</pre>
                </p-accordion-content>
              </p-accordion-panel>
            }
            @if (selectedExecution.resultDataJson) {
              <p-accordion-panel value="result">
                <p-accordion-header>Ergebnisdaten</p-accordion-header>
                <p-accordion-content>
                  <pre class="text-sm p-3 surface-ground border-round overflow-auto" style="max-height: 20rem;">{{ formatJson(selectedExecution.resultDataJson) }}</pre>
                </p-accordion-content>
              </p-accordion-panel>
            }
          </p-accordion>
        </div>
      }
    </div>
  }

} @else {
  <!-- Pre-initialization loading -->
  <div class="flex flex-column gap-3 p-3">
    <p-skeleton width="100%" height="2.5rem"></p-skeleton>
    <p-skeleton width="100%" height="12rem"></p-skeleton>
  </div>
}
